'use client';

type paramsToSet = {
	paramName: string;
	options:
		| { paramValue: string; event?: never; append?: boolean }
		| {
				paramValue?: never;
				event: { target: { value: string } };
				append?: boolean;
		  };
};

import type { estypes } from '@elastic/elasticsearch';
import CollapsibleDrawer from 'components/collapsible-drawer';
import Subsection from 'components/containers/sub-section';
import Button from 'components/controls/buttons/button';
import ClearableFilters, {
	ClearAllButton,
} from 'components/controls/filters/clearable-filters';
import { SearchBarCombobox } from 'components/controls/search-bar-combobox';
import SimpleSelect from 'components/controls/simple-select';
import CopyToClipboard from 'components/copy-to-clipboard';
import PaginationControls from 'components/pagination';
import NoResults from 'components/search/no-results';
import {
	PER_PAGE,
	MAX_RESULTS_COUNT,
	searchFields,
	sortByFields,
} from 'helpers/collection-search';
import { scrollToRef } from 'helpers/react';
import { displayYear } from 'lib/display-year';
import { friendlyYearRange } from 'lib/friendly-year-range';
import { searchYearsAreValid } from 'lib/searchYearsAreValid';
import { useSearchParams } from 'next/navigation';
import { usePathname, useRouter } from 'src/navigation';

import { useEffect, useRef, useState } from 'react';
import {
	JsonView,
	collapseAllNested,
	defaultStyles,
} from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import styles from './collection-search-app.module.scss';
import FiltersIcon from 'assets/icons/filters.svg';
import CollectionSearchLayout from 'components/collection-search/collection-search-layout';
import CheckboxGroupHeading from 'components/controls/checkbox-group-heading';
import CheckboxWithLabel from 'components/controls/checkbox-with-label';
import Tooltip from 'components/tooltip';
import { Link } from 'src/navigation';
import { DateControls } from '../date-controls';
import { isDev } from 'lib/is-dev';
import { sortDepartmentsByName } from 'lib/sortDepartmentNames';
import type { SearchMode } from 'lib/semantic-search/constants';
import {
	trackCollectionSearchClearedAll,
	trackCollectionSearchFilterChanged,
	trackCollectionSearchPaginated,
	trackCollectionSearchResultClicked,
	trackCollectionSearchResultsLoaded,
	trackCollectionSearchSubmitted,
} from 'lib/analytics/trackSearch';
import ObjectResultsGrid from '../results-grid';
import Toggle from 'components/controls/toggle';
import type { AdvancedCollectionSearchAggs } from 'src/app/[locale]/(navigation)/art/collection/search/(search page)/build-aggs';
import kebabCase from 'lodash/kebabCase';
import type { IElasticObjectSearchSource } from 'helpers/collection/model';
import SearchToolTip from '../search-tooltip';
import ContentWarning from 'components/collection/content-warning';

const showOnlyOptions = {
	highlights: 'Highlights',
	onDisplay: 'On view',
};

// these are also under the `showOnly` param, but separated visually by a divider
const imageOptions = {
	withImage: 'Has image',
	openAccess: 'Has Open Access image',
	has3d: 'Has 3D image',
};

const dateOptions = {
	yearFrom: 'From',
	yearTo: 'To',
};

const termFilterOptions = {
	artist: 'Artist / culture',
	material: 'Object type / material',
	geolocation: 'Geographic location',
	department: 'Department',
};

const allFieldsSearchPlaceholder =
	'Search by subject (e.g. landscapes, woman in black), artist, title, date, object number, and more';

const toolTips = {
	openAccess: (
		<Tooltip
			id={'filters-open-access-tooltip'}
			jsxDescription={
				<section>
					<p>
						As part of the Met&apos;s{' '}
						<Link href="/about-the-met/policies-and-documents/open-access">
							Open Access policy
						</Link>
						, you can freely copy, modify and distribute this image, even for
						commercial purposes.
					</p>
					<h4>API</h4>
					<p>
						Public domain data for this object can also be accessed using the
						Met&apos;s{' '}
						<a target="_new" href="https://metmuseum.github.io/">
							Open Access API
						</a>
					</p>
				</section>
			}
		/>
	),
};

interface ICollectionSearchAppProps {
	results: estypes.SearchResponse<
		IElasticObjectSearchSource,
		AdvancedCollectionSearchAggs
	>;
	explanation: estypes.ExplainResponse;
	searchMode?: SearchMode;
}

const CollectionSearchApp = (props: ICollectionSearchAppProps) => {
	const { results, explanation, searchMode } = props;

	const router = useRouter();
	const pathname = usePathname();
	const browserSearchParams = useSearchParams();
	const browserSearchParamsString = browserSearchParams.toString();
	const topRef = useRef<HTMLElement>(null);
	const lastTrackedSearchResultsQuerystring = useRef<string | null>(null);

	// local state for keeping UI instantly responsive
	// uses a stringified version of the params because, y'know, React doesn't deep compare
	const [localSearchParamsViaString, setLocalSearchParamsViaString] = useState(
		browserSearchParams.toString(),
	);
	const isSearching =
		browserSearchParams.toString() !== localSearchParamsViaString;
	const localSearchParams = new URLSearchParams(localSearchParamsViaString);

	const [hideControls, setHideControls] = useState(true);

	const yearFrom = localSearchParams.get('yearFrom');
	const yearTo = localSearchParams.get('yearTo');
	const yearFromNumeric = parseInt(yearFrom);
	const yearToNumeric = parseInt(yearTo);
	const displayYearFrom = displayYear(yearFromNumeric);
	const displayYearTo = displayYear(yearToNumeric) || 'present';
	const yearsAreValid = searchYearsAreValid({
		yearFrom,
		yearTo,
		yearFromNumeric,
		yearToNumeric,
	});

	const searchFieldName = searchFields
		.find(
			(field) => field.value === (localSearchParams.get('searchField') || ''),
		)
		?.name.toLowerCase();

	const placeholderText =
		searchFieldName === `all fields`
			? allFieldsSearchPlaceholder
			: `Search ${searchFieldName}`;
	const typeSafeTotalResults =
		typeof results.hits.total === 'number'
			? results.hits.total
			: results.hits.total.value;
	const showResults = typeSafeTotalResults > 0 && yearsAreValid;

	const paramsToCountForClearAll = [
		localSearchParams.getAll('artist'),
		localSearchParams.getAll('material'),
		localSearchParams.getAll('geolocation'),
		localSearchParams.getAll('department'),
		localSearchParams.has('yearFrom') || localSearchParams.has('yearTo'), // these act like 1 filter, so count them as 1
		localSearchParams.getAll('showOnly'),
	]
		.flat()
		.filter(Boolean);
	const showClearAll = paramsToCountForClearAll.length > 1; // don't show clear all if there's only 1, needs 2

	const updateParamsAndSearch = (
		newParams: URLSearchParams,
		resultOffset = 0,
	) => {
		resultOffset === 0
			? newParams.delete('offset')
			: newParams.set('offset', `${resultOffset}`); // If changing anything besides "page/offset" - return to first page

		[...newParams.entries()].forEach(([key, value]) => {
			//Remove empty params
			if (!value) newParams.delete(key);
		});

		const nextSearchQuerystring = newParams.toString();
		setLocalSearchParamsViaString(nextSearchQuerystring);
		return nextSearchQuerystring;
	};

	useEffect(() => {
		// This makes the app do the search and change 👇
		if (browserSearchParamsString !== localSearchParamsViaString) {
			router.replace(`${pathname}?${localSearchParamsViaString}`, {
				scroll: false, // we set scroll: false because we do a manual scrollIntoView on pagination change and align main to top instead of document top
			});
		}
	}, [pathname, router, browserSearchParamsString, localSearchParamsViaString]);

	useEffect(() => {
		// wait for url/results to sync, then track once per querystring to avoid dupes
		const currentResultsQuerystring = browserSearchParamsString;

		if (isSearching) {
			return;
		}

		if (!yearsAreValid) {
			lastTrackedSearchResultsQuerystring.current = null;
			return;
		}

		if (
			lastTrackedSearchResultsQuerystring.current === currentResultsQuerystring
		) {
			return;
		}

		trackCollectionSearchResultsLoaded({
			searchQuerystring: currentResultsQuerystring,
			queryTookMs: results.took,
			resultsCount: typeSafeTotalResults,
			searchMode,
		});
		lastTrackedSearchResultsQuerystring.current = currentResultsQuerystring;
	}, [
		browserSearchParamsString,
		isSearching,
		results.took,
		searchMode,
		typeSafeTotalResults,
		yearsAreValid,
	]);

	// TODO: we should probably refactor this whole component to useReducer for state
	const setParam = (
		paramName: paramsToSet['paramName'],
		options: paramsToSet['options'],
	) => {
		const params = new URLSearchParams(localSearchParams);

		let value =
			typeof options?.paramValue === 'undefined'
				? options.event.target.value
				: options.paramValue;
		// unfortunately no HTML input can handle numbers the way we need
		// so we have to clean this up here
		if (['yearFrom', 'yearTo'].includes(paramName)) {
			if (typeof value === 'string') {
				value = value.slice(0, 8).replaceAll(/[^0-9-]/g, '');
			}
		}

		if (options?.append) {
			// don't append if it would be a duplicate. ie, don't do: a=1&a=1
			if (!params.getAll(paramName).includes(value)) {
				params.append(paramName, value);
			}
		} else {
			params.set(paramName, value);
		}

		const nextSearchQuerystring = updateParamsAndSearch(params);
		trackCollectionSearchFilterChanged({
			filterName: paramName,
			searchMode,
			searchQuerystring: nextSearchQuerystring,
		});

		return nextSearchQuerystring;
	};

	const unsetParam = (paramName: string, values?: string[]) => {
		const params = new URLSearchParams(localSearchParams);
		if (values) {
			values.forEach((value) => params.delete(paramName, value));
		} else {
			params.delete(paramName);
		}
		const nextSearchQuerystring = updateParamsAndSearch(params);
		trackCollectionSearchFilterChanged({
			filterName: paramName,
			searchMode,
			searchQuerystring: nextSearchQuerystring,
		});

		return nextSearchQuerystring;
	};

	const removeParams = (...paramNames: string[]) => {
		const params = new URLSearchParams(localSearchParams);
		paramNames.forEach((paramName) => params.delete(paramName));
		updateParamsAndSearch(params);
	};

	const handleRemoveClearableFilter = (event, facetName) => {
		unsetParam(facetName, [event.target.value]);
	};

	const handleYearFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setParam('yearFrom', { event });
	};

	const handleYearToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setParam('yearTo', { event });
	};

	const handleYearReset = () => {
		const paramsObject = new URLSearchParams(localSearchParams);
		paramsObject.delete('yearFrom');
		paramsObject.delete('yearTo');
		const nextSearchQuerystring = updateParamsAndSearch(paramsObject);
		trackCollectionSearchFilterChanged({
			filterName: 'yearRange',
			searchMode,
			searchQuerystring: nextSearchQuerystring,
		});
	};

	const handleShowOnlyChange = (event) => {
		const target = event.target;
		const isChecked = target.type === 'checkbox' ? target.checked : false;
		const name = target.value;

		const paramsObject = new URLSearchParams(localSearchParams);
		if (isChecked) {
			paramsObject.append('showOnly', name);
		} else {
			paramsObject.delete('showOnly', name);
		}

		const nextSearchQuerystring = updateParamsAndSearch(paramsObject);
		trackCollectionSearchFilterChanged({
			filterName: 'showOnly',
			searchMode,
			searchQuerystring: nextSearchQuerystring,
		});
	};

	const handleFilterButtonClick = () => {
		setHideControls(!hideControls);
	};

	const handlePaginationChange = (event) => {
		scrollToRef(topRef, 'auto'); // we do this instead of letting the router scroll

		const paramsObject = new URLSearchParams(localSearchParams);
		const newOffset = Math.max(
			0,
			parseInt(event.currentTarget.value) +
				(parseInt(localSearchParams.get('offset')) || 0),
		);
		const nextSearchQuerystring = updateParamsAndSearch(
			paramsObject,
			newOffset,
		);
		trackCollectionSearchPaginated({
			searchMode,
			searchQuerystring: nextSearchQuerystring,
		});
	};

	const handleClearAll = () => {
		const q = localSearchParams.get('q');
		const newParams = new URLSearchParams();
		q && newParams.append('q', q);
		const nextSearchQuerystring = updateParamsAndSearch(newParams);
		trackCollectionSearchClearedAll({
			searchMode,
			searchQuerystring: nextSearchQuerystring,
		});
	};

	const handleResultClick = (
		hit: (typeof results.hits.hits)[number],
		index: number,
	) => {
		const offset = parseInt(localSearchParams.get('offset')) || 0;

		trackCollectionSearchResultClicked({
			objectId: hit._source.crdId,
			objectTitle: Array.isArray(hit._source.title)
				? hit._source.title[0]
				: hit._source.title,
			resultRank: offset + index + 1, // global rank
			searchMode,
			searchQuerystring: browserSearchParamsString,
		});
	};

	const dateParamsCount = Object.keys(dateOptions)
		.map((paramName) => localSearchParams.get(paramName))
		// intentional use of != over !== to also filter undefined / parity with lodash isNil()
		.filter((value) => value != null).length;

	// the showing x-y of z message
	const offset = parseInt(localSearchParams.get('offset')) || 0;
	const startIndex = offset + 1;
	const endIndex =
		offset + PER_PAGE < typeSafeTotalResults
			? offset + PER_PAGE
			: startIndex + (startIndex + PER_PAGE - typeSafeTotalResults);

	const ofTotalMessage =
		typeSafeTotalResults >= MAX_RESULTS_COUNT
			? `${MAX_RESULTS_COUNT.toLocaleString()}+ results`
			: `${typeSafeTotalResults.toLocaleString()} results`;

	const showingNumberOfResultsMessage =
		typeSafeTotalResults === 1
			? 'One result'
			: typeSafeTotalResults < PER_PAGE
				? `${typeSafeTotalResults} results`
				: `${startIndex}-${endIndex} of ${ofTotalMessage}`;

	return (
		<main
			ref={topRef}
			className={`${styles.main} ${isSearching ? styles.isLoading : ''}`}
		>
			{isDev() && (
				<CopyToClipboard
					text={JSON.stringify(props.results.profile)}
					message="copy profiling data"
				/>
			)}
			{isDev() && (
				<JsonView
					data={props.results}
					style={defaultStyles}
					shouldExpandNode={collapseAllNested}
				/>
			)}

			<div className={styles.titleWrapper}>
				{/* TODO: technically the page should have an h1 if it will have an h2. instead it only has this h2, but the h1 size is too big */}
				<h2>Search for Art</h2>
				<SearchToolTip searchQuerystring={browserSearchParamsString} />
			</div>
			<div className={styles.barWrapper}>
				<SimpleSelect
					id={'search-field'}
					initialValue={localSearchParams.get('searchField') || ''}
					handleChange={(event) => setParam('searchField', { event })}
					options={searchFields}
				/>
				<SearchBarCombobox
					handleComboboxSelection={({ field, value }) => {
						setParam(field, {
							paramValue: value,
							append: field !== 'q', // search would crash if multiple q params
						});
					}}
					suggestionEndpoint={'/art/collection/search/suggest'}
					buttonBehavior="submit"
					clearOnSelect={true}
					id={'advanced-collection-search-text-input'}
					initialQuery={localSearchParams.get('q') || ''}
					isSearching={isSearching}
					mode="commandPalette"
					name={'q'}
					onClear={() => {
						// set sort back to relevance
						removeParams('q', 'sortBy');
					}}
					onSubmit={(query: string) => {
						trackCollectionSearchSubmitted({
							searchMode,
							searchOrigin: 'Collection Search',
							searchTerm: query,
						});
						setParam('q', { paramValue: query });
					}}
					placeholderText={placeholderText}
				/>
			</div>
			<section className={styles.allTopFiltersContainer}>
				<section className={styles.quickFiltersContainer}>
					<Button
						data-testid="toggle-show-filter-panel"
						className={styles.filterButton}
						appearance="tertiary"
						onClick={handleFilterButtonClick}
					>
						Filters <FiltersIcon />
					</Button>
					<div className={styles.showOnlyOptions}>
						{Object.keys(showOnlyOptions).map((paramName) => (
							<CheckboxWithLabel
								key={paramName}
								checked={localSearchParams
									.getAll('showOnly')
									.includes(paramName)}
								id={kebabCase(`quick-filter-show-only-${paramName}`)}
								name={showOnlyOptions[paramName]}
								onChange={handleShowOnlyChange}
								toolTip={toolTips[paramName]}
								value={paramName}
							/>
						))}
					</div>
					<div className={styles.imageOptions}>
						{Object.keys(imageOptions).map((paramName) => (
							<CheckboxWithLabel
								key={paramName}
								checked={localSearchParams
									.getAll('showOnly')
									.includes(paramName)}
								id={kebabCase(`quick-filter-show-only-${paramName}`)}
								name={imageOptions[paramName]}
								onChange={handleShowOnlyChange}
								toolTip={toolTips[paramName]}
								value={paramName}
							/>
						))}
					</div>
				</section>
				<section className={styles.activeFilters}>
					<Button
						className={`${styles.filterButton} ${styles.mobileOnly}`}
						appearance="tertiary"
						onClick={handleFilterButtonClick}
					>
						Filters <FiltersIcon />
					</Button>
					{['artist', 'material', 'geolocation', 'department'].map(
						(facetName) => {
							return localSearchParams
								.getAll(facetName)
								.map((selectedOption) => {
									return (
										<ClearableFilters
											key={`${facetName}-${selectedOption}`}
											groupName={facetName}
											handleClose={handleRemoveClearableFilter}
											selectedValues={[
												{
													label: selectedOption,
													value: selectedOption,
												},
											]}
										/>
									);
								});
						},
					)}

					{[showOnlyOptions, imageOptions].map((group) =>
						Object.keys(group)
							.filter((option) =>
								localSearchParams.getAll('showOnly').includes(option),
							)
							.map((key) => (
								<ClearableFilters
									groupName={group[key]}
									key={group[key]}
									handleClose={handleShowOnlyChange}
									selectedValues={[{ label: group[key], value: key }]}
								/>
							)),
					)}

					{(yearFrom || yearTo) && (
						<ClearableFilters
							groupName={'years'} // this doesn't really matter. our pattern breaks down now that we're combining two params into one.
							key={'years'}
							handleClose={handleYearReset}
							selectedValues={[
								{
									label: friendlyYearRange({
										yearFrom,
										yearTo,
										displayYearFrom,
										displayYearTo,
										yearsAreValid,
									}),
									value: yearFrom || yearTo,
								},
							]}
						/>
					)}
					{showClearAll && <ClearAllButton handleClearAll={handleClearAll} />}
				</section>
			</section>
			{showResults && (
				<section className={styles.sortResults}>
					<div
						className={styles.totalResults}
						data-testid="total-results-message-wrapper"
						data-total-search-results={typeSafeTotalResults}
					>
						<span>{showingNumberOfResultsMessage}</span>
						{localSearchParams.get('q') && (
							<span>
								{' '}
								for{' '}
								<span className={styles.resultsMessageQuery}>
									{localSearchParams.get('q')}
								</span>
							</span>
						)}
					</div>
					{isDev() && (
						<div className={styles.took}>
							Took {results.took.toLocaleString()}ms
						</div>
					)}
					<div className={styles.sortControl}>
						<span className={styles.sectionTitle}>Sort by:</span>
						<SimpleSelect
							id={'sort-by'}
							initialValue={localSearchParams.get('sortBy') || 'Relevance'}
							options={sortByFields}
							handleChange={(event) => setParam('sortBy', { event })}
						/>
					</div>
				</section>
			)}
			<CollectionSearchLayout
				handleOpenButton={() => setHideControls(false)}
				handleCloseButton={() => setHideControls(true)}
				hideControls={hideControls}
				results={
					<div>
						{showResults ? (
							<ObjectResultsGrid
								isLoading={isSearching}
								explanation={explanation}
								isDev={isDev()}
								objects={results?.hits?.hits || []}
								onExplainChange={(event) => {
									// @ts-expect-error who cares what the exact event type is, jeez
									setParam('explain', { event });
								}}
								onResultClick={handleResultClick}
							/>
						) : (
							<Subsection>
								{/* TODO: this currently never handles "no results" for just filtering, ie, when searchQueryParam is undefined. Also needs i18n */}
								<NoResults
									title={`No results found.`}
									body="We couldn't find any matches. Try checking your spelling, using different key words, or broadening your search."
								/>
							</Subsection>
						)}
						<Subsection>
							{showResults && (
								<PaginationControls
									offset={parseInt(localSearchParams.get('offset')) || 0}
									handlePaginationChange={handlePaginationChange}
									perPage={PER_PAGE}
									totalResults={typeSafeTotalResults}
								/>
							)}
							<div className={styles.rttWrapper}>
								<Button
									appearance="secondary"
									onKeyDown={(event) =>
										event.key === 'Enter' && scrollToRef(topRef)
									}
									onClick={() => scrollToRef(topRef, 'smooth')}
								>
									Return To Top
								</Button>
							</div>
						</Subsection>
						<ContentWarning />
					</div>
				}
				controls={
					<div className={styles.controlWrapper}>
						<div className={styles.scrollingContainer}>
							<CheckboxGroupHeading
								standalone
								name="Show only"
								count={
									localSearchParams
										.getAll('showOnly')
										.filter((param) =>
											Object.keys(showOnlyOptions).includes(param),
										).length
								}
							/>
							<div className={styles.checkboxGroups}>
								{['highlights', 'onDisplay'].map((paramName) => (
									<CheckboxWithLabel
										key={paramName}
										checked={localSearchParams
											.getAll('showOnly')
											.includes(paramName)}
										count={
											results.aggregations.show_only.buckets.buckets[paramName]
												.doc_count
										}
										id={kebabCase(`filter-show-only-${paramName}`)}
										name={showOnlyOptions[paramName]}
										onChange={handleShowOnlyChange}
										value={paramName}
									/>
								))}
							</div>

							<CollapsibleDrawer
								open={false}
								heading={
									<CheckboxGroupHeading
										name="Images"
										count={
											localSearchParams
												.getAll('showOnly')
												.filter((param) =>
													Object.keys(imageOptions).includes(param),
												).length
										}
									/>
								}
							>
								<div className={styles.checkboxGroups}>
									{['withImage', 'has3d', 'openAccess'].map((paramName) => (
										<CheckboxWithLabel
											key={paramName}
											checked={localSearchParams
												.getAll('showOnly')
												.includes(paramName)}
											count={
												results.aggregations.show_only.buckets.buckets[
													paramName
												].doc_count
											}
											id={kebabCase(`filter-show-only-${paramName}`)}
											name={imageOptions[paramName]}
											onChange={handleShowOnlyChange}
											toolTip={toolTips[paramName]}
											value={paramName}
										/>
									))}
								</div>
							</CollapsibleDrawer>
							<CollapsibleDrawer
								open={false}
								heading={
									<CheckboxGroupHeading name="Date" count={dateParamsCount} />
								}
							>
								<div className={styles.checkboxGroups}>
									<DateControls
										{...{
											localSearchParams,
											handleYearFromChange,
											handleYearToChange,
											yearsAreValid,
											yearsMin: results.aggregations.years.stats.min,
											yearsMax: results.aggregations.years.stats.max,
										}}
									/>
								</div>
							</CollapsibleDrawer>

							{['artist', 'material', 'geolocation'].map((filterId) => {
								const isMatchAll = localSearchParams
									.getAll('matchAll')
									.includes(filterId);

								return (
									<CollapsibleDrawer
										key={filterId}
										open={false}
										heading={
											<CheckboxGroupHeading
												name={termFilterOptions[filterId]}
												count={localSearchParams.getAll(filterId).length}
											/>
										}
									>
										<div
											className={styles.drawerContainer}
											data-testid={`${filterId}-filter-drawer-contents`}
										>
											<SearchBarCombobox
												forwardParams={localSearchParamsViaString}
												suggestionEndpoint={'/art/collection/search/suggest'}
												suggestionField={filterId}
												handleComboboxSelection={(value) => {
													if (value?.value) {
														setParam(filterId, {
															paramValue: value.value,
															append: true,
														});
													}
												}}
												id={filterId}
												onSubmit={(query: string) =>
													setParam(filterId, {
														paramValue: query,
														append: true,
													})
												}
											/>

											{filterId !== 'artist' && (
												<div style={{ lineHeight: 1 }}>
													<div className={styles.toggleCopy}>Match</div>
													&nbsp;
													<Toggle
														small={true}
														label={'Show results matching'}
														onChange={(e) => {
															if (e === 'all') {
																setParam('matchAll', {
																	append: true,
																	paramValue: filterId,
																});
															} else {
																unsetParam('matchAll', [filterId]);
															}
														}}
														options={[
															{ value: 'any', label: 'Any' },
															{ value: 'all', label: 'All' },
														]}
														initialActiveOptionIndex={isMatchAll ? 1 : 0}
													/>{' '}
													<div className={styles.toggleCopy}>
														selected filters.
													</div>
												</div>
											)}
											<div className={styles.checkboxGroups}>
												{localSearchParams.getAll(filterId).map((value) => {
													return (
														<CheckboxWithLabel
															key={`${filterId}-${value}`}
															checked={true}
															count={
																results.aggregations[
																	filterId
																].buckets.buckets.find(
																	(bucket) => bucket.key === value,
																)?.doc_count ??
																results.aggregations[filterId]?.selected
																	?.buckets[value]?.doc_count
															}
															id={kebabCase(`filter-${filterId}-${value}`)}
															name={value}
															onChange={() => {
																unsetParam(filterId, [value]);
															}}
															value={filterId}
														/>
													);
												})}

												{results.aggregations[filterId].buckets.buckets
													.filter(
														({ key }) =>
															// exclude options already selected
															!localSearchParams.getAll(filterId).includes(key),
													)
													.sort((a, b) => b.doc_count - a.doc_count)
													.map(({ key, doc_count }) => {
														return (
															<CheckboxWithLabel
																key={`${filterId}-${key}`}
																checked={false}
																count={doc_count}
																id={kebabCase(
																	kebabCase(`filter-${filterId}-${key}`),
																)}
																name={key}
																onChange={() => {
																	setParam(filterId, {
																		paramValue: key,
																		append: true,
																	});
																}}
																value={filterId}
															/>
														);
													})}
											</div>
										</div>
									</CollapsibleDrawer>
								);
							})}

							{/* Departments doesn't follow the pattern of the others. The list of departments remains stable, even as the search narrows, so it requires its own unique markup */}
							<CollapsibleDrawer
								key={'department'}
								open={false}
								heading={
									<CheckboxGroupHeading
										name={termFilterOptions['department']}
										count={localSearchParams.getAll('department').length}
									/>
								}
							>
								<div className={styles.checkboxGroups}>
									{sortDepartmentsByName<{
										displayName: string;
										key: string;
										doc_count: number;
									}>(
										results.aggregations.department.buckets.buckets.map(
											({ key, doc_count }) => ({
												displayName: key,
												key,
												doc_count,
											}),
										),
									).map(({ key, doc_count }) => {
										const activeDepartments =
											localSearchParams.getAll('department');

										return (
											<CheckboxWithLabel
												key={`department-${key}`}
												checked={activeDepartments.includes(key)}
												count={doc_count}
												id={kebabCase(`filter-department-${key}`)}
												name={key}
												onChange={(event) => {
													event.target.checked
														? setParam('department', {
																paramValue: key,
																append: true,
															})
														: unsetParam('department', [key]);
												}}
												value={'department'}
											/>
										);
									})}
								</div>
							</CollapsibleDrawer>
						</div>
					</div>
				}
			/>
		</main>
	);
};

export { CollectionSearchApp };
